import { convertPdfToPngsConverter } from 'features/fde/PdfToPngConverter'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  FC,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { usePdfPageCount } from './usePdfPageCount'

interface InteractiveCropViewerProps {
  url: string
  isPdf?: boolean
  height?: number
  currentPage?: number
  pageRotations?: Record<string, number>
  onCropChange: (
    rect: { x: number; y: number; width: number; height: number } | undefined
  ) => void
  onPageChange?: (page: number) => void
}

interface Point {
  x: number
  y: number
}

interface DrawingRect {
  start: Point
  end: Point
}

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 4px;
  overflow: hidden;
`

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  max-width: 100%;
  max-height: 100%;
`

const StyledImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  background: white;
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 16px;
`

const SVGOverlay = styled.svg<{ $width: number; $height: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  pointer-events: none;
`

const InteractionLayer = styled.div<{ $width: number; $height: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  cursor: crosshair;
`

const Hint = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  pointer-events: none;
  z-index: 10;
`

const PageNavigation = styled.div`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  z-index: 10;
`

const NavButton = styled.button<{ disabled?: boolean }>`
  background: ${({ disabled }) =>
    disabled ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
  border: none;
  color: ${({ disabled }) => (disabled ? '#666' : 'white')};
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
  }
`

const PageInfo = styled.div`
  font-weight: 500;
  min-width: 80px;
  text-align: center;
`

export const InteractiveCropViewer: FC<InteractiveCropViewerProps> = ({
  url,
  isPdf = false,
  height = 1000,
  currentPage = 1,
  pageRotations,
  onCropChange,
  onPageChange,
}) => {
  const [internalPage, setInternalPage] = useState(currentPage)

  // Sync internal page with prop
  useEffect(() => {
    setInternalPage(currentPage)
  }, [currentPage])

  // Derive rotation from internalPage so display and save are always in sync
  const rotation = pageRotations?.[String(internalPage)] ?? 0

  // Get total page count for PDFs
  const { pageCount } = usePdfPageCount(isPdf ? url : undefined)

  // For PDFs: render the page with our rotation as an override (not additive to native).
  // This matches PdfViewer's behaviour and avoids double-rotation with native PDF rotation.
  // For non-PDFs: apply rotation via canvas.
  const [displayUrl, setDisplayUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!url) {
      setDisplayUrl(null)
      return
    }

    if (isPdf) {
      let cancelled = false
      convertPdfToPngsConverter(url, {
        scale: 2,
        pageRotations,
      })
        .then((dataUrls) => {
          if (!cancelled) setDisplayUrl(dataUrls[internalPage - 1] ?? null)
        })
        .catch(() => {
          if (!cancelled) setDisplayUrl(null)
        })
      return () => {
        cancelled = true
      }
    }

    // Non-PDF image
    if (rotation === 0) {
      setDisplayUrl(url)
      return
    }

    const img = new Image()
    img.onload = () => {
      const rad = (rotation * Math.PI) / 180
      const canvas = document.createElement('canvas')
      if (rotation === 90 || rotation === 270) {
        canvas.width = img.height
        canvas.height = img.width
      } else {
        canvas.width = img.width
        canvas.height = img.height
      }
      const ctx = canvas.getContext('2d')!
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate(rad)
      ctx.drawImage(img, -img.width / 2, -img.height / 2)
      setDisplayUrl(canvas.toDataURL())
    }
    img.src = url
  }, [url, isPdf, internalPage, rotation, pageRotations])

  const [displayedSize, setDisplayedSize] = useState<{
    width: number
    height: number
  }>()
  const [naturalSize, setNaturalSize] = useState<{
    width: number
    height: number
  }>()
  const [drawingRect, setDrawingRect] = useState<DrawingRect>()
  const [isDrawing, setIsDrawing] = useState(false)
  const [completedRect, setCompletedRect] = useState<DrawingRect>()
  const [imageLoaded, setImageLoaded] = useState(false)

  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    setImageLoaded(false)
    setNaturalSize(undefined)
    setDisplayedSize(undefined)
    setDrawingRect(undefined)
    setCompletedRect(undefined)
  }, [url, internalPage, rotation])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage < 1 || newPage > pageCount) return
      setInternalPage(newPage)
      onPageChange?.(newPage)
    },
    [pageCount, onPageChange]
  )

  // Track displayed size changes
  useEffect(() => {
    const updateDisplayedSize = () => {
      if (imageRef.current) {
        setDisplayedSize({
          width: imageRef.current.width,
          height: imageRef.current.height,
        })
      }
    }

    updateDisplayedSize()

    const resizeObserver = new ResizeObserver(updateDisplayedSize)
    if (imageRef.current) {
      resizeObserver.observe(imageRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [displayUrl])

  const getMousePosition = useCallback(
    (e: { clientX: number; clientY: number }): Point | null => {
      if (!imageRef.current || !naturalSize) return null

      const rect = imageRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Convert to natural image coordinates
      const naturalX = (x / rect.width) * naturalSize.width
      const naturalY = (y / rect.height) * naturalSize.height

      return {
        x: Math.max(0, Math.min(naturalX, naturalSize.width)),
        y: Math.max(0, Math.min(naturalY, naturalSize.height)),
      }
    },
    [naturalSize]
  )

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      const point = getMousePosition(e)
      if (!point) return

      setIsDrawing(true)
      setDrawingRect({
        start: point,
        end: point,
      })
      setCompletedRect(undefined)
      onCropChange(undefined)
    },
    [getMousePosition, onCropChange]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDrawing || !drawingRect) return

      const point = getMousePosition(e)
      if (!point) return

      setDrawingRect({
        ...drawingRect,
        end: point,
      })
    },
    [isDrawing, drawingRect, getMousePosition]
  )

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !drawingRect || !naturalSize) {
      setIsDrawing(false)
      setDrawingRect(undefined)
      return
    }

    const minSize = 50 // Minimum crop size
    const width = Math.abs(drawingRect.end.x - drawingRect.start.x)
    const height = Math.abs(drawingRect.end.y - drawingRect.start.y)

    if (width >= minSize && height >= minSize) {
      setCompletedRect(drawingRect)
      onCropChange({
        x: Math.min(drawingRect.start.x, drawingRect.end.x),
        y: Math.min(drawingRect.start.y, drawingRect.end.y),
        width: Math.abs(drawingRect.end.x - drawingRect.start.x),
        height: Math.abs(drawingRect.end.y - drawingRect.start.y),
      })
    }

    setIsDrawing(false)
    setDrawingRect(undefined)
  }, [isDrawing, drawingRect, naturalSize, onCropChange])

  useEffect(() => {
    if (!isDrawing) return
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDrawing, handleMouseMove, handleMouseUp])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDrawing(false)
        setDrawingRect(undefined)
        setCompletedRect(undefined)
        onCropChange(undefined)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onCropChange])

  const renderCropRect = useCallback(() => {
    const rect = completedRect || drawingRect
    if (!rect || !displayedSize || !naturalSize) return null

    const scale = displayedSize.width / naturalSize.width
    const x = Math.min(rect.start.x, rect.end.x) * scale
    const y = Math.min(rect.start.y, rect.end.y) * scale
    const width = Math.abs(rect.end.x - rect.start.x) * scale
    const height = Math.abs(rect.end.y - rect.start.y) * scale

    const isCompleted = !!completedRect

    return (
      <>
        {/* Darken area outside crop */}
        <rect
          x={0}
          y={0}
          width={displayedSize.width}
          height={displayedSize.height}
          fill='black'
          fillOpacity={0.5}
          pointerEvents='none'
        />

        {/* Clear area inside crop */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill='transparent'
          pointerEvents='none'
        />

        {/* Crop rectangle border */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill='none'
          stroke={isCompleted ? '#52c41a' : '#3B82F6'}
          strokeWidth={isCompleted ? 3 : 2}
          strokeDasharray={isCompleted ? undefined : '5,5'}
          pointerEvents='none'
        />

        {/* Size indicator */}
        {isCompleted && (
          <text
            x={x + width / 2}
            y={y - 10}
            fill='#52c41a'
            fontSize='14'
            fontWeight='600'
            textAnchor='middle'
            pointerEvents='none'
            style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
            {Math.round(width / scale)} × {Math.round(height / scale)} px
          </text>
        )}
      </>
    )
  }, [drawingRect, completedRect, displayedSize, naturalSize])

  if (isPdf && !displayUrl) {
    return (
      <Container style={{ height: height ? `${height}px` : '100%' }}>
        <div
          style={{
            color: '#999',
            padding: '48px',
            textAlign: 'center',
            fontSize: '16px',
          }}>
          Converting PDF to image for cropping...
          <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
            This may take a few seconds for large PDFs
          </div>
        </div>
      </Container>
    )
  }

  if (!displayUrl) {
    return (
      <Container style={{ height: height ? `${height}px` : '100%' }}>
        <div style={{ color: '#999' }}>Loading document...</div>
      </Container>
    )
  }

  return (
    <Container style={{ height: height ? `${height}px` : '100%' }}>
      <Hint>
        {completedRect
          ? '✓ Crop region selected. Adjust by drawing again or click Save on the right.'
          : '← Click and drag to select a region to crop'}
      </Hint>

      {isPdf && pageCount > 1 && (
        <PageNavigation>
          <NavButton
            disabled={internalPage <= 1}
            onClick={() => handlePageChange(internalPage - 1)}>
            <ChevronLeft size={20} />
          </NavButton>
          <PageInfo>
            Page {internalPage} of {pageCount}
          </PageInfo>
          <NavButton
            disabled={internalPage >= pageCount}
            onClick={() => handlePageChange(internalPage + 1)}>
            <ChevronRight size={20} />
          </NavButton>
        </PageNavigation>
      )}

      <ImageContainer ref={containerRef}>
        <StyledImage
          ref={imageRef}
          src={displayUrl}
          alt='Source document'
          onLoad={(e) => {
            const img = e.currentTarget
            setNaturalSize({
              width: img.naturalWidth,
              height: img.naturalHeight,
            })
            setDisplayedSize({ width: img.width, height: img.height })
            setImageLoaded(true)
          }}
          onError={(e) => {
            console.error('[InteractiveCropViewer] Image failed to load:', e)
          }}
          style={{ opacity: imageLoaded ? 1 : 0 }}
        />

        {!imageLoaded && <LoadingOverlay>Loading image...</LoadingOverlay>}

        {displayedSize && naturalSize && imageLoaded && (
          <>
            <SVGOverlay
              $width={displayedSize.width}
              $height={displayedSize.height}
              width={displayedSize.width}
              height={displayedSize.height}>
              {renderCropRect()}
            </SVGOverlay>

            <InteractionLayer
              $width={displayedSize.width}
              $height={displayedSize.height}
              onMouseDown={handleMouseDown}
            />
          </>
        )}
      </ImageContainer>
    </Container>
  )
}
